import {
  type J5RegistryV1ArtifactQueryServiceArtifactListArtifactListRequest,
  J5RegistryV1ArtifactQueryServiceArtifactListFilterableFields,
  type J5RegistryV1ArtifactQueryServiceArtifactListResponse,
  J5RegistryV1ArtifactQueryServiceArtifactListSearchableFields,
  J5RegistryV1ArtifactQueryServiceArtifactListSortableFields,
  J5RegistryV1ArtifactStatus,
} from '@interxfi/types';
import { useRealmAuthIsAuthenticated } from '../context';
import { j5RegistryV1ArtifactQueryServiceArtifactList } from '../generated/client-functions/j5-registry-v1-artifact-query-service-artifact-list';
import {
  buildJ5RegistryV1ArtifactQueryServiceArtifactListKey,
  j5RegistryV1ArtifactQueryServiceArtifactListResponseEntity,
  useJ5RegistryV1ArtifactQueryServiceArtifactList,
} from '../generated/hooks/j5-registry-v1-artifact-query-service-artifact-list';
import type { ApiSourceBundle } from './api-source';
import { getRegistryBaseUrl } from './registry';

export type DocsArtifactBundle = ApiSourceBundle | 'getting-started' | 'guides';

export function getDocsListArtifactsRequest(bundle: DocsArtifactBundle | DocsArtifactBundle[], version: string) {
  return {
    query: {
      sorts: [
        {
          field: J5RegistryV1ArtifactQueryServiceArtifactListSortableFields.DataBundleIndex,
        },
      ],
      searches: [
        {
          field: J5RegistryV1ArtifactQueryServiceArtifactListSearchableFields.DataContentType,
          value: 'text/markdown',
          wholeWord: true,
        },
      ],
      filters: [
        {
          field: {
            name: J5RegistryV1ArtifactQueryServiceArtifactListFilterableFields.Version,
            type: { value: version },
          },
        },
        {
          field: {
            name: J5RegistryV1ArtifactQueryServiceArtifactListFilterableFields.Status,
            type: { value: J5RegistryV1ArtifactStatus.Active },
          },
        },
        {
          field: {
            name: J5RegistryV1ArtifactQueryServiceArtifactListFilterableFields.Bundle,
            type: Array.isArray(bundle) ? { in: { values: Array.from(new Set(bundle)) } } : { value: bundle },
          },
        },
      ],
    },
  } satisfies J5RegistryV1ArtifactQueryServiceArtifactListArtifactListRequest;
}

// TODO: update codegen to build this when there's time
export function getDocsListArtifactsQueryOptions(request: J5RegistryV1ArtifactQueryServiceArtifactListArtifactListRequest | undefined) {
  return {
    queryKey: buildJ5RegistryV1ArtifactQueryServiceArtifactListKey(request),
    queryFn: async ({ pageParam }: any = {}) =>
      j5RegistryV1ArtifactQueryServiceArtifactList(
        getRegistryBaseUrl(),
        request || pageParam
          ? { ...request, page: pageParam || request?.page?.pageSize ? { token: pageParam, pageSize: request?.page?.pageSize } : undefined }
          : undefined,
      ),
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    refetchOnMount: false,
    getNextPageParam: (response: J5RegistryV1ArtifactQueryServiceArtifactListResponse | undefined) => response?.page?.nextToken,
    initialPageParam: undefined,
    meta: { normalizationSchema: j5RegistryV1ArtifactQueryServiceArtifactListResponseEntity },
  } as const;
}

export function useListDocsArtifacts(request?: J5RegistryV1ArtifactQueryServiceArtifactListArtifactListRequest | undefined) {
  const isAuthenticated = useRealmAuthIsAuthenticated();

  return useJ5RegistryV1ArtifactQueryServiceArtifactList(request, {
    ...getDocsListArtifactsQueryOptions(request),
    enabled: Boolean(request && isAuthenticated),
  });
}
